import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Grid,
  Paper,
  IconButton,
  Collapse,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import WorkflowTile, { getWorkflowIconColor, getWorkflowSteps, estimateWorkflowCosts } from './WorkflowTile';
import { getIconByName } from '../IconSelector';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

const THEME_COLOR = '#009688';
const THEME_COLOR_LIGHT = '#e0f2f1';

// Selected workflow summary card
const SelectedWorkflowCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  backgroundColor: THEME_COLOR_LIGHT,
  border: `1px solid ${THEME_COLOR}`,
  borderRadius: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

// Icon container for selected workflow
const IconBox = styled(Box)(({ color }) => ({
  width: 44,
  height: 44,
  borderRadius: 10,
  backgroundColor: color || THEME_COLOR,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: `0 2px 8px ${color}40`,
}));

const WorkflowSelectionPanel = ({
  workflows = [],
  selectedWorkflowId = null,
  onWorkflowSelect,
  autoCollapse = true,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isExpanded, setIsExpanded] = useState(true);

  // Auto-collapse when workflow is selected
  useEffect(() => {
    if (selectedWorkflowId && autoCollapse) {
      setIsExpanded(false);
    }
  }, [selectedWorkflowId, autoCollapse]);

  // Normalize workflows data - handle different API structures
  const normalizedWorkflows = useMemo(() => {
    return workflows.map(item => {
      // Handle nested workflow object (from assignments API)
      const workflow = item.workflow || item;

      // Parse definition if it's a string
      let definition = workflow.definition;
      if (typeof definition === 'string') {
        try {
          definition = JSON.parse(definition);
        } catch (e) {
          definition = null;
        }
      }

      return {
        id: workflow.id || item.workflowId,
        name: workflow.name || workflow.workflowName || 'Unnamed Workflow',
        description: workflow.description || '',
        type: workflow.type || workflow.workflowType || 'default',
        icon: workflow.icon,
        color: workflow.color,
        // Include definition for steps extraction
        definition: definition,
        steps: workflow.steps || (definition && definition.steps) || [],
        // Preserve original data for selection callback
        _original: item,
      };
    });
  }, [workflows]);

  // Filter workflows based on search term
  const filteredWorkflows = useMemo(() => {
    if (!searchTerm.trim()) {
      return normalizedWorkflows;
    }
    const lowerSearch = searchTerm.toLowerCase();
    return normalizedWorkflows.filter(workflow =>
      workflow.name.toLowerCase().includes(lowerSearch) ||
      (workflow.description && workflow.description.toLowerCase().includes(lowerSearch))
    );
  }, [normalizedWorkflows, searchTerm]);

  // Get selected workflow details
  const selectedWorkflow = useMemo(() => {
    if (!selectedWorkflowId) return null;
    return normalizedWorkflows.find(w => w.id === selectedWorkflowId);
  }, [selectedWorkflowId, normalizedWorkflows]);

  // Handle workflow selection
  const handleWorkflowClick = (workflow) => {
    // Pass the original data structure back
    onWorkflowSelect && onWorkflowSelect(workflow._original || workflow);
  };

  // Handle expand/collapse
  const handleChangeWorkflow = () => {
    setIsExpanded(true);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm('');
  };

  // Get icon/color for selected workflow
  const selectedIconColor = selectedWorkflow ? getWorkflowIconColor(selectedWorkflow) : null;

  // Get steps and costs for selected workflow
  const selectedSteps = useMemo(() => {
    return selectedWorkflow ? getWorkflowSteps(selectedWorkflow) : [];
  }, [selectedWorkflow]);

  const selectedCosts = useMemo(() => {
    return estimateWorkflowCosts(selectedSteps);
  }, [selectedSteps]);

  return (
    <Box>
      {/* Selected Workflow Summary */}
      <Collapse in={Boolean(selectedWorkflow)}>
        {selectedWorkflow && selectedIconColor && (
          <SelectedWorkflowCard elevation={0}>
            <IconBox color={selectedIconColor.color}>
              {React.createElement(getIconByName(selectedIconColor.icon), {
                sx: { fontSize: 24, color: '#fff' },
              })}
            </IconBox>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <CheckCircleIcon sx={{ fontSize: 16, color: THEME_COLOR }} />
                <Typography variant="caption" sx={{ color: THEME_COLOR, fontWeight: 600 }}>
                  Selected Workflow
                </Typography>
              </Box>
              <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                {selectedWorkflow.name}
              </Typography>
              {selectedWorkflow.description && (
                <Typography sx={{ fontSize: 12, color: '#616161', mb: 0.5 }}>
                  {selectedWorkflow.description}
                </Typography>
              )}
              {/* Services and Cost Info */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                {selectedSteps.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <FormatListBulletedIcon sx={{ fontSize: 14, color: '#757575' }} />
                    <Typography sx={{ fontSize: 11, color: '#757575' }}>
                      {selectedSteps.length} service{selectedSteps.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                )}
                {selectedCosts.total > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AttachMoneyIcon sx={{ fontSize: 14, color: '#f57c00' }} />
                    <Typography sx={{ fontSize: 11, color: '#f57c00', fontWeight: 500 }}>
                      Est. AED {selectedCosts.total} (Govt: {selectedCosts.govtCharge} + Service: {selectedCosts.serviceCharge})
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
            {/* Change Workflow Button - shown when collapsed */}
            {!isExpanded && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                onClick={handleChangeWorkflow}
                sx={{
                  borderColor: THEME_COLOR,
                  color: THEME_COLOR,
                  textTransform: 'none',
                  fontWeight: 600,
                  minWidth: 100,
                  '&:hover': {
                    borderColor: THEME_COLOR,
                    backgroundColor: `${THEME_COLOR}10`,
                  },
                }}
              >
                Change
              </Button>
            )}
          </SelectedWorkflowCard>
        )}
      </Collapse>

      {/* Collapsible Section: Search and Workflows */}
      <Collapse in={isExpanded || !selectedWorkflowId}>
        {/* Search Input */}
        <TextField
          fullWidth
          placeholder="Search workflows..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: '#fff',
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: THEME_COLOR,
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#9e9e9e' }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch}>
                  <ClearIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Workflows Grid */}
        {filteredWorkflows.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
              color: '#757575',
              border: '1px dashed #e0e0e0',
              borderRadius: 2,
            }}
          >
            <Typography>
              {searchTerm
                ? `No workflows found matching "${searchTerm}"`
                : 'No workflows available'}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {filteredWorkflows.map((workflow) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={workflow.id}>
                <WorkflowTile
                  workflow={workflow}
                  selected={selectedWorkflowId === workflow.id}
                  onClick={handleWorkflowClick}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Collapse>
    </Box>
  );
};

export default WorkflowSelectionPanel;
